import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../utils";
import { useTranslation } from "react-i18next";
import { Typography } from "../Typography";
import { Button } from "../Button";
import { Select, TSelectOption } from "@/components/forms";
import { useMemo } from "react";

type TPaginationProps = {
  totalRecordCount: number;
  page: number;
  pageCount: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRows: number) => void;
  disablePrev: boolean;
  disableNext: boolean;
  className?: string;
  id?: string;
};

const CommonPaginationUI = ({
  totalRecordCount,
  page,
  pageCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  disablePrev,
  disableNext,
  className,
  id,
}: TPaginationProps) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation("common");
  const isRTL = i18n.language === "ar";
  const options = useMemo(() => {
    return [
      {
        value: "5",
        label: `5 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
      {
        value: "10",
        label: `10 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
      {
        value: "20",
        label: `20 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
      {
        value: "30",
        label: `30 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
      {
        value: "50",
        label: `50 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
      {
        value: "100",
        label: `100 ${t("PAGINATION.ROWS_PER_PAGE")}`,
      },
    ];
  }, [t]);
  return (
    <div
      className={cn(
        "flex items-center justify-between  border-t pt-2",
        className,
      )}
      data-testid={id ? `${id}-pagination` : "pagination"}
    >
      <Typography className="text-foreground">
        {t("PAGINATION.TOTAL_RECORDS")}: {totalRecordCount}
      </Typography>
      <div className="flex items-center gap-4">
        <div className="flex items-center">
          <Select
            name={`${id}-rows-per-page-select`}
            options={options}
            value={
              options?.find((item) => Number(item.value) === rowsPerPage) ??
              null
            }
            onChange={(option: TSelectOption) =>
              onRowsPerPageChange(Number(option.value))
            }
            getOptionLabel={(option: TSelectOption) => option.label}
            getOptionValue={(option: TSelectOption) => option.value}
            placeholder={t("FILTER.SELECT_PERIOD")}
            className="text-sm"
            isClearable={false}
          />
        </div>
        <div className="flex items-center rounded-lg border bg-background">
          <Typography
            variant={"p"}
            className={cn("px-3 py-1 text-foreground", !isRTL && "border-r")}
          >
            {page + 1} / {pageCount}
          </Typography>
          <div className="flex">
            <Button
              variant="ghost"
              size={"icon"}
              onClick={() => onPageChange(page - 1)}
              disabled={disablePrev}
              title={t("PAGINATION.PREVIOUS")}
              aria-label="previousPage"
              className={`border-0 border-r px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background ${
                disablePrev ? "cursor-not-allowed" : "cursor-pointer"
              }, ${isRTL && "border-l"}`}
              id={`${id}-previous-button`}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size={"icon"}
              onClick={() => onPageChange(page + 1)}
              disabled={disableNext}
              title={t("PAGINATION.NEXT")}
              aria-label="nextPage"
              id={`${id}-next-button`}
              className={`rounded-r-lg px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background ${
                disableNext ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonPaginationUI;
